/*  UI  */
import { Flex } from '@auviknetworks/iceberg-ui';
import { Skeleton, Typography, useTheme } from '@mui/material';
import { FontSizes } from 'application/Shared/Constants';

/*  React  */
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { type KPIPanel as KPIPanelType } from '../types';
import { Panel } from './Panel';
import { usePanelContext } from './PanelContext';

/**
 * Skeleton for the KPI panel.
 * This is used to show a loading state for the KPI panel.
 */
export const KPIPanelSkeleton = () => {
    const theme = useTheme();

    return (
        <Flex padding="0 24px" flexDirection="column">
            <Flex alignItems="center">
                {/* Value */}
                <Skeleton variant="text" width={168} height={50} />
            </Flex>
            <Flex
                alignItems="center"
                marginTop="24px"
                borderTop={`1px solid ${theme.palette.border.card}`}
                paddingTop="8px"
            >
                {/* Icon */}
                <Skeleton
                    variant="circular"
                    width={16}
                    height={16}
                    sx={{ marginRight: '8px' }}
                />
                {/* Last updated */}
                <Skeleton variant="text" width={120} height={20} />
            </Flex>
        </Flex>
    );
};

/**
 * Content for the KPI panel.
 * This is used to show the KPI content.
 */
const KPIContent = () => {
    const { t } = useTranslation();
    const { palette } = useTheme();

    const {
        queryResults: { data },
        schema: { format = {} },
        setNoData
    } = usePanelContext<KPIPanelType>();

    useEffect(() => {
        if (!data || !data.length) {
            setNoData(true);
        } else {
            setNoData(false);
        }
    }, [data, setNoData]);

    const [{ value = null }] = data?.length ? data : [{}];
    const { unit, suffix, prefix, decimals, defaultValue } = format;

    const noData = value == null;

    return (
        <Flex
            data-testid="kpiPanel"
            sx={{ flexDirection: 'column', height: '100%', padding: '0 24px' }}
        >
            <Flex
                sx={{
                    alignItems: 'center',
                    flexGrow: 1,
                    height: '100%',
                    justifyContent: 'center'
                }}
            >
                {prefix && (
                    <Typography
                        component="span"
                        fontSize="14px"
                        marginRight="8px"
                    >
                        {prefix}
                    </Typography>
                )}
                {/* We show value only if it is not undefined */}
                <Typography
                    component="span"
                    fontSize={noData ? FontSizes[2] : '35px'}
                    color={
                        noData
                            ? palette.text.cardKPINoData
                            : palette.text.cardKPI
                    }
                >
                    {/* if decimals set round the value to the number of decimals */}
                    {!noData && decimals && Number.isFinite(value)
                        ? value.toFixed(decimals)
                        : value}
                    {noData && (defaultValue || t('Common.noData'))}
                </Typography>
                {unit && value && (
                    <Typography
                        component="span"
                        fontSize="16px"
                        marginLeft="4px"
                    >
                        {unit}
                    </Typography>
                )}
                {suffix && (
                    <Typography
                        component="span"
                        fontSize="14px"
                        marginLeft="4px"
                    >
                        {suffix}
                    </Typography>
                )}
            </Flex>
        </Flex>
    );
};

export const KPIPanel = ({ panel }: { panel: KPIPanelType }) => (
    <Panel<KPIPanelType>
        panel={{ ...panel, format: { titleSize: 'small', ...panel.format } }}
        canFullScreen={false}
        skeleton={<KPIPanelSkeleton />}
    >
        <KPIContent />
    </Panel>
);
